import { AuthResponse } from '@/types/auth';
import { SupabaseAuthService } from './supabase-auth.service';
import { ApiAuthService } from './api-auth.service';
import { StorageService } from './storage.service';

const SITE_URL = window.location.origin;

export class AuthService {
  private supabaseAuth: SupabaseAuthService;
  private apiAuth: ApiAuthService;

  constructor() {
    this.supabaseAuth = new SupabaseAuthService();
    this.apiAuth = new ApiAuthService();
  }

  async requestMagicLink(email: string, redirectTo?: string): Promise<void> {
    const redirectUrl = redirectTo || `${SITE_URL}/auth/callback`;
    await this.supabaseAuth.requestMagicLink(email, redirectUrl);
  }

  async verifyMagicLink(
    email: string,
    token: string,
    type: string = 'magiclink',
  ): Promise<AuthResponse> {
    // Verify token with Supabase
    await this.supabaseAuth.verifyMagicLinkToken(email, token, type);

    // Get session from Supabase
    const { data: sessionData } = await this.supabaseAuth.getSession();

    if (!sessionData.session) {
      throw new Error('Failed to get session');
    }

    // Get token hash from session
    const tokenHash = sessionData.session.access_token || token;

    // Call backend API to get JWT token
    const authResponse = await this.apiAuth.verifyMagicLink(
      email,
      tokenHash,
      type,
    );

    // Save to storage
    StorageService.setAccessToken(authResponse.accessToken);
    StorageService.setRefreshToken(authResponse.refreshToken);
    StorageService.setUser(authResponse.user);

    return authResponse;
  }

  async signInWithGoogle(redirectTo?: string): Promise<void> {
    const redirectUrl = redirectTo || `${SITE_URL}/auth/callback`;
    const url = await this.apiAuth.getGoogleOAuthUrl(redirectUrl);
    window.location.href = url;
  }

  async handleOAuthCallback(
    code: string,
    state?: string,
    redirectUri?: string,
  ): Promise<AuthResponse> {
    const authResponse = await this.apiAuth.handleOAuthCallback(
      code,
      state,
      redirectUri,
    );

    StorageService.setAccessToken(authResponse.accessToken);
    StorageService.setRefreshToken(authResponse.refreshToken);
    StorageService.setUser(authResponse.user);

    return authResponse;
  }

  async signOut(): Promise<void> {
    await this.supabaseAuth.signOut();
    StorageService.clear();
  }

  async restoreSession(): Promise<AuthResponse | null> {
    const refreshToken = StorageService.getRefreshToken();
    if (!refreshToken) return null;

    const authResponse = await this.apiAuth.refreshSession(refreshToken);
    StorageService.setAccessToken(authResponse.accessToken);
    StorageService.setRefreshToken(authResponse.refreshToken);
    StorageService.setUser(authResponse.user);
    return authResponse;
  }

  getCurrentUser(): { accessToken: string; user: any } | null {
    const accessToken = StorageService.getAccessToken();
    const user = StorageService.getUser();

    if (!accessToken || !user) {
      return null;
    }

    return { accessToken, user };
  }
}

// Export singleton instance
export const authService = new AuthService();
